import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Loader2, Moon, Banknote, CreditCard, Car, Store } from "lucide-react";
import { toast } from "sonner";

import api, { formatApiError } from "@/lib/api";
import { listSucursales } from "@/services/sucursalesService";
import DashboardLayout from "@/components/DashboardLayout";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtDate } from "@/lib/datetime";

const money = (v) => `$${Number(v || 0).toFixed(2)}`;
const fmtFecha = (d) => (d && d !== "—" ? fmtDate(d, { day: "2-digit", month: "short", year: "numeric" }) : "—");

export default function PernoctasAdmin() {
  const [data, setData] = useState({ total_vehiculos: 0, total_monto: 0, monto_efectivo: 0, monto_tarjeta: 0, filas: [] });
  const [loading, setLoading] = useState(true);
  const [sucursales, setSucursales] = useState([]);
  const [sucFiltro, setSucFiltro] = useState("all");
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (sucFiltro !== "all") params.set("id_sucursal", sucFiltro);
      if (desde) params.set("fecha_desde", desde);
      if (hasta) params.set("fecha_hasta", hasta);
      const qs = params.toString();
      const { data: res } = await api.get(`/operativa/pernoctas-reporte${qs ? `?${qs}` : ""}`);
      setData(res || { total_vehiculos: 0, total_monto: 0, monto_efectivo: 0, monto_tarjeta: 0, filas: [] });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "No se pudo cargar el reporte de pernoctas");
    } finally {
      setLoading(false);
    }
  }, [sucFiltro, desde, hasta]);

  useEffect(() => {
    listSucursales().then(({ data }) => setSucursales(data || [])).catch(() => {});
  }, []);
  useEffect(() => { cargar(); }, [cargar]);

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto p-5 lg:p-8" data-testid="pernoctas-admin-page">
        <div className="mb-6">
          <h1 className="font-display text-3xl font-black tracking-tight text-[#0A0A0A] flex items-center gap-2">
            <Moon className="h-7 w-7 text-[#6366F1]" /> Pensiones Nocturnas
          </h1>
          <p className="text-sm text-[#52525B] mt-1">Vehículos pernoctados e ingresos por pensión nocturna, auditados por sucursal y fecha de corte.</p>
        </div>

        {/* Filtros */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
          <div>
            <Label className="text-[10px] tracking-wider uppercase font-bold text-[#52525B]">Sucursal</Label>
            <Select value={sucFiltro} onValueChange={setSucFiltro}>
              <SelectTrigger className="h-10 rounded-none border-[#0A0A0A] mt-1" data-testid="pernoctas-suc-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las sucursales</SelectItem>
                {sucursales.map((s) => (
                  <SelectItem key={s.id_sucursal} value={s.id_sucursal}>{s.nombre}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] tracking-wider uppercase font-bold text-[#52525B]">Desde</Label>
            <Input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} data-testid="pernoctas-desde" className="h-10 rounded-none border-[#0A0A0A] mt-1" />
          </div>
          <div>
            <Label className="text-[10px] tracking-wider uppercase font-bold text-[#52525B]">Hasta</Label>
            <Input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} data-testid="pernoctas-hasta" className="h-10 rounded-none border-[#0A0A0A] mt-1" />
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
          <Kpi icon={Car} color="#6366F1" label="Vehículos pernoctados" value={data.total_vehiculos} testid="kpi-total-vehiculos" />
          <Kpi icon={Moon} color="#0A0A0A" label="Recaudación total" value={money(data.total_monto)} testid="kpi-total-monto" />
          <div className="border border-[#e4e4e7] bg-white p-4" data-testid="kpi-desglose">
            <p className="text-[10px] uppercase tracking-wider font-bold text-[#52525B] mb-2">Desglose recaudación</p>
            <div className="flex items-center justify-between text-sm">
              <span className="flex items-center gap-1.5 text-[#047857]"><Banknote className="h-4 w-4" /> Efectivo</span>
              <span className="font-mono font-bold text-[#047857]" data-testid="kpi-efectivo">{money(data.monto_efectivo)}</span>
            </div>
            <div className="flex items-center justify-between text-sm mt-1">
              <span className="flex items-center gap-1.5 text-[#0369A1]"><CreditCard className="h-4 w-4" /> Tarjeta</span>
              <span className="font-mono font-bold text-[#0369A1]" data-testid="kpi-tarjeta">{money(data.monto_tarjeta)}</span>
            </div>
          </div>
        </div>

        {/* Tabla histórica */}
        {loading ? (
          <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#6366F1]" /></div>
        ) : (data.filas || []).length === 0 ? (
          <div className="border border-dashed border-[#d4d4d8] py-16 text-center text-[#52525B]" data-testid="pernoctas-empty">No hay pernoctas registradas en el periodo seleccionado.</div>
        ) : (
          <div className="border border-[#e4e4e7] bg-white overflow-x-auto" data-testid="pernoctas-tabla">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#FAFAFA] text-left text-[10px] uppercase tracking-wider font-bold text-[#52525B]">
                  <th className="px-4 py-2.5">Sucursal</th>
                  <th className="px-4 py-2.5">Fecha corte</th>
                  <th className="px-4 py-2.5 text-center">Vehículos</th>
                  <th className="px-4 py-2.5 text-right">Efectivo</th>
                  <th className="px-4 py-2.5 text-right">Tarjeta</th>
                  <th className="px-4 py-2.5 text-right">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#f1f1f3]">
                {data.filas.map((f, i) => (
                  <motion.tr key={`${f.id_sucursal}-${f.fecha}-${i}`} initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                    className="hover:bg-[#FAFAFA]" data-testid={`pernoctas-fila-${i}`}>
                    <td className="px-4 py-2.5 font-bold flex items-center gap-1.5"><Store className="h-3.5 w-3.5 text-[#71717A]" />{f.sucursal_nombre || "—"}</td>
                    <td className="px-4 py-2.5 text-[#52525B]">{fmtFecha(f.fecha)}</td>
                    <td className="px-4 py-2.5 text-center font-mono font-bold text-[#6366F1]">{f.cantidad}</td>
                    <td className="px-4 py-2.5 text-right font-mono text-[#047857]">{money(f.ingreso_efectivo)}</td>
                    <td className="px-4 py-2.5 text-right font-mono text-[#0369A1]">{money(f.ingreso_tarjeta)}</td>
                    <td className="px-4 py-2.5 text-right font-mono font-black">{money(f.ingreso_total)}</td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

function Kpi({ icon: Icon, color, label, value, testid }) {
  return (
    <div className="border border-[#e4e4e7] bg-white p-4" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold text-[#52525B]">
        {Icon ? <Icon className="h-4 w-4" style={color ? { color } : undefined} /> : null}{label}
      </div>
      <p className="font-display text-2xl font-black text-[#0A0A0A] mt-1" style={color ? { color } : undefined}>{value}</p>
    </div>
  );
}
